import { useQuery } from '@tanstack/react-query';

import { platformSettings } from '@/shared/api/resources';
import { Badge } from '@/shared/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';

import { formatBytes } from '../documents/envio';

/**
 * Limites da plataforma, so para leitura (P-PLAT).
 *
 * A administracao da plataforma decide, para cada empresa, a cota de disco, o limite
 * de sinistros por mes e as marcas do OnPoint. A empresa so le o que foi decidido: a
 * gravacao fica no /admin, e o servidor nao tem outro caminho.
 */
export function PlatformLimitsCard() {
  const consulta = useQuery({
    queryKey: ['tenant', 'platform-settings'],
    queryFn: ({ signal }) => platformSettings.current(signal),
  });

  const dados = consulta.data;
  return (
    <Card>
      <CardHeader
        title="Limites da plataforma"
        description="Definidos pela administracao da plataforma."
      />
      <CardBody className="flex flex-col gap-3 text-sm">
        {consulta.isError ? (
          <p className="text-ink-500">Nao foi possivel carregar os limites.</p>
        ) : !dados ? (
          <p className="text-ink-500">Carregando os limites.</p>
        ) : (
          <>
            <Linha
              rotulo="Cota de disco"
              valor={
                dados.limit_disk_space_gb !== null
                  ? `${dados.limit_disk_space_gb} GB${
                      dados.allow_exceed_disk_space_limit ? ', pode passar' : ''
                    }`
                  : 'Sem cota'
              }
            />
            <Linha
              rotulo="Em uso"
              valor={formatBytes(dados.disk_used_bytes)}
              selo={dados.disk_over_limit ? 'Acima da cota' : undefined}
            />
            <Linha
              rotulo="Limite de sinistros por mes"
              valor={
                dados.limit_claims_per_month !== null
                  ? `${dados.limit_claims_per_month}${
                      dados.allow_exceed_claims_limit ? ', pode passar' : ''
                    }`
                  : 'Sem limite'
              }
            />
            <Linha
              rotulo="API externa de sinistros"
              valor={dados.has_external_claim_upload_api ? 'Ligada' : 'Desligada'}
            />
            <Linha rotulo="Empresa nova" valor={dados.is_new ? 'Sim' : 'Nao'} />
            <Linha
              rotulo="Ambiente do portal"
              valor={dados.portal_environment ? 'Configurado' : 'Sem configuracao'}
            />
            <p className="mt-1 text-2xs text-ink-500">
              So a cota de disco tem efeito: o envio de documento que passaria dela e
              recusado, ou segue com aviso quando a plataforma permite passar. Os demais
              ficam registrados ate a regra ser confirmada.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Linha({
  rotulo,
  valor,
  selo,
}: {
  rotulo: string;
  valor: string;
  selo?: string | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-ink-500">{rotulo}</span>
      <span className="flex items-center gap-2 truncate font-medium text-ink-900">
        {valor}
        {selo ? <Badge tone="danger">{selo}</Badge> : null}
      </span>
    </div>
  );
}
